/* ==========================================================================
   Upsell builder: behaviour for snippets/product-upsell-builder.liquid.
   The snippet documents the config, the activation contract and the pricing
   rule; this file implements them.

   Loaded by snippets/product-form.liquid on products with builder entries or
   a Pair entry the snippet falls back to. A
   PDP running the v2 buy-box arm renders product-form twice, so this file can
   arrive twice: the guard below makes the second copy a no-op, and initAll()
   binds each [data-upsell-builder] once.
   ========================================================================== */
(function () {
  'use strict';

  if (window.RayconUpsellBuilder) return;

  var LOG = '[Upsell Builder]';
  // Shared with the placebo coupon box (snippets/discount-code-box-apply.liquid),
  // so "applied" means the same thing on both.
  var COUPON_KEY = 'rcCouponApplied';

  // The theme's own formatter and format (layout/theme.liquid sets
  // theme.moneyFormat per market), so prices read exactly like the money
  // snippet's. The local fallback covers the same placeholders for the rare
  // case slate is not there; tags in the format are dropped because prices are
  // written as text.
  function money(cents) {
    var format = (window.theme && theme.moneyFormat) || '${{amount}}';
    try {
      if (window.slate && slate.Currency && slate.Currency.formatMoney) {
        return String(slate.Currency.formatMoney(cents, format)).replace(/<[^>]*>/g, '');
      }
    } catch (e) { /* fall through to the local formatter */ }
    function group(value, decimals, thousands, decimal) {
      var parts = (value / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var amount;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': amount = group(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': amount = group(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = group(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': amount = group(cents, 2, "'", '.'); break;
      default: amount = group(cents, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  }

  // Shopify's rule, per unit: floor the saving to the cent. Multiply by the
  // whole percent BEFORE dividing: 0.3 is inexact as a double, 30 is not.
  function discounted(cents, pct) {
    return cents - Math.floor(cents * pct / 100);
  }

  // A getComputedStyle walk rather than offsetParent: the module is revealed by a
  // stylesheet rule or by an ancestor (the v2 buy box) being shown, and neither
  // shows up on the element's own inline style.
  function displayed(el) {
    for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
      if (window.getComputedStyle(n).display === 'none') return false;
    }
    return true;
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  // Prices render through the theme's money snippet, which wraps them in
  // <span class="money">; write inside it, as theme.js does.
  function setMoney(el, cents) {
    if (!el) return;
    setText(el.querySelector('.money') || el, money(cents));
  }

  function setHidden(el, hidden) {
    if (el && el.hidden !== hidden) el.hidden = hidden;
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function purchasable(btn) {
    if (!btn || btn.disabled || btn.classList.contains('disabled')) return false;
    var str = (window.theme && theme.strings) || {};
    var label = btn.querySelector('[data-add-to-cart-text]');
    var text = label ? label.textContent.trim() : '';
    return text !== (str.soldOut || 'Sold Out') && text !== (str.unavailable || 'Unavailable');
  }

  function Builder(root) {
    this.root = root;
    this.uid = root.id;
    this.form = root.closest('form');
    this.container = root.closest('[data-section-type]') || this.form || document.body;

    try {
      this.cfg = JSON.parse(root.querySelector('[data-ub-config]').textContent);
    } catch (e) {
      console.error(LOG, 'Unreadable config JSON, module left dormant.', e);
      return;
    }

    var cfg = this.cfg;
    this.tiers = (cfg.tiers || []).map(Number);
    this.soloPct = Number(cfg.soloPct) || 0;
    // Pair fallback only (see the snippet): each side's own percent once the
    // add-on is in, as the Pair card prices the same pair.
    this.pair = cfg.pairPct
      ? { main: Number(cfg.pairPct.main) || 0, addon: Number(cfg.pairPct.addon) || 0 }
      : null;
    this.rows = Array.prototype.slice.call(root.querySelectorAll('[data-ub-row]'));
    this.prices = this.rows.map(function (row) {
      return parseInt(row.getAttribute('data-ub-price'), 10) || 0;
    });
    this.on = this.rows.map(function () { return true; });
    this.mode = cfg.defaultMode === 'solo' ? 'solo' : 'bundle';
    // Start from the variant the form holds now: theme.js initialises first and
    // may already have moved off the one Liquid rendered.
    var idInput = this.form ? this.form.querySelector('[name="id"]') : null;
    this.variantId = idInput && cfg.variants && cfg.variants[idInput.value]
      ? String(idInput.value)
      : String(cfg.mainVariant);
    this.couponKey = cfg.couponKey || 'upsell-builder';
    this.couponApplied = false;
    try {
      this.couponApplied = window.sessionStorage.getItem(COUPON_KEY) === this.couponKey;
    } catch (e) { /* storage unavailable */ }
    this.active = false;
    this.openModal = null;
    this.modalTrigger = null;

    // The highest percent any item count reaches, and the fewest add-ons that
    // reach it: the target the hint counts down to.
    this.maxPct = 0;
    this.maxAt = 0;
    for (var k = 0; k <= this.rows.length; k++) {
      var pct = this.tierPct(k);
      if (pct > this.maxPct) {
        this.maxPct = pct;
        this.maxAt = k;
      }
    }

    var qty = this.form ? this.form.querySelector('[data-ub-qty]') : null;
    // "Learn more" popups: one Pair-card product popup per row
    // (snippets/product-upsell-pair-modal.liquid). Adopted into <body>, as the
    // Pair card does in activate(): a form ancestor (and .ub, a size container)
    // would otherwise be the containing block of the position:fixed overlay.
    this.modals = this.rows.map(function (row) {
      var modal = row.querySelector('[data-pair-modal]');
      if (modal && document.body) document.body.appendChild(modal);
      return modal;
    });
    this.el = {
      cards: root.querySelectorAll('[data-ub-card]'),
      radios: root.querySelectorAll('.ub-card__input'),
      soloPrice: root.querySelector('[data-ub-solo-price]'),
      soloWas: root.querySelector('[data-ub-solo-was]'),
      apply: root.querySelector('[data-ub-apply]'),
      applied: root.querySelector('[data-ub-applied]'),
      panel: root.querySelector('[data-ub-panel]'),
      mainImgs: root.querySelectorAll('[data-ub-main-img]'),
      mainName: root.querySelector('[data-ub-main-name]'),
      mainPrice: root.querySelector('[data-ub-main-price]'),
      fill: root.querySelector('[data-ub-meter-fill]'),
      tierLabels: root.querySelectorAll('[data-ub-tier-label]'),
      cardBadge: root.querySelector('[data-ub-card-badge]'),
      badge: root.querySelector('[data-ub-save-badge]'),
      total: root.querySelector('[data-ub-total]'),
      was: root.querySelector('[data-ub-was]'),
      hint: root.querySelector('[data-ub-hint]'),
      qty: qty,
      qtyValue: qty ? qty.querySelector('[data-ub-qty-value]') : null,
      qtyMinus: qty ? qty.querySelector('[data-ub-qty-minus]') : null,
      qtyPlus: qty ? qty.querySelector('[data-ub-qty-plus]') : null
    };

    each(cfg.warnings, function (w) {
      console.warn(LOG, w, 'Check product.metafields.upsell.config.');
    });

    this.bind();
    this.checkActive(true);
    console.log(LOG, 'init', {
      id: this.uid,
      addons: this.rows.length,
      tiers: this.tiers,
      soloPct: this.soloPct,
      pairPct: this.pair,
      mode: this.mode,
      active: this.active
    });
  }

  // Percent for a given number of add-ons switched on. Missing tiers repeat the last.
  Builder.prototype.tierPct = function (addons) {
    var t = this.tiers;
    if (!t.length) return 0;
    return t[Math.min(addons, t.length - 1)] || 0;
  };

  // Percent off one line. Tiers price every line alike; the Pair fallback
  // prices each side at its own percent once the add-on is in.
  Builder.prototype.linePct = function (isMain, count) {
    if (this.pair && count > 0) return isMain ? this.pair.main : this.pair.addon;
    return this.tierPct(count);
  };

  // One unit of the bundle with the given switches: what it charges and what
  // it costs before discounts.
  Builder.prototype.sumsFor = function (p0, on) {
    var self = this;
    var count = on.filter(Boolean).length;
    var sums = { total: discounted(p0, this.linePct(true, count)), orig: p0 };
    this.prices.forEach(function (price, i) {
      if (!on[i]) return;
      sums.total += discounted(price, self.linePct(false, count));
      sums.orig += price;
    });
    return sums;
  };

  // The percent a state advertises: its tier, or in the Pair fallback the
  // saving actually computed, as the Pair card derives its headline.
  Builder.prototype.shownPct = function (count, sums) {
    if (!this.pair || count === 0) return this.tierPct(count);
    return sums.orig > 0 ? Math.round((sums.orig - sums.total) * 100 / sums.orig) : 0;
  };

  Builder.prototype.onCount = function () {
    return this.on.filter(Boolean).length;
  };

  Builder.prototype.variant = function () {
    var map = this.cfg.variants || {};
    var v = map[this.variantId];
    if (!v && this.form) {
      var sel = this.form.querySelector('[name="id"]');
      if (sel && map[sel.value]) {
        this.variantId = String(sel.value);
        v = map[this.variantId];
      }
    }
    return v || null;
  };

  // The quantity theme.js submits: the first [name="quantity"] in the section,
  // read exactly the way assets/theme.js reads it at add-to-cart.
  Builder.prototype.qty = function () {
    var input = this.container.querySelector('[name="quantity"]');
    return Math.max(1, parseInt(input && input.value, 10) || 1);
  };

  Builder.prototype.qtyMax = function () {
    var sel = this.container.querySelector('[data-quantity-select]');
    if (!sel) return 10;
    var max = 1;
    each(sel.options, function (opt) {
      var n = parseInt(opt.value, 10);
      if (n > max) max = n;
    });
    return max;
  };

  // Drive the theme's own quantity control, so everything already listening to
  // it keeps working: theme.js copies the dropdown into [name="quantity"], and
  // reads that input when it builds the cart request.
  Builder.prototype.setQty = function (q) {
    q = Math.max(1, Math.min(this.qtyMax(), q));
    var sel = this.container.querySelector('[data-quantity-select]');
    if (sel && String(sel.value) !== String(q)) {
      sel.value = String(q);
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    each(this.container.querySelectorAll('[name="quantity"]'), function (input) {
      if (String(input.value) !== String(q)) {
        input.value = String(q);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    console.log(LOG, 'quantity', q);
    this.renderQty();
    this.renderAtc();
  };

  Builder.prototype.setMode = function (mode) {
    if (mode !== 'solo' && mode !== 'bundle') return;
    if (mode === this.mode) {
      this.render();
      return;
    }
    // Choosing "Bundle & Save" again starts from the full bundle, as designed.
    if (mode === 'bundle') this.on = this.rows.map(function () { return true; });
    this.mode = mode;
    console.log(LOG, 'mode', mode);
    this.render();
  };

  Builder.prototype.toggle = function (i) {
    if (this.mode !== 'bundle' || i < 0 || i >= this.rows.length) return;
    this.on[i] = !this.on[i];
    console.log(LOG, 'add-on', i, this.on[i] ? 'on' : 'off');
    this.render();
  };

  // Placebo, like the coupon box: the automatic discount applies at checkout
  // either way. Applying also picks the card the button sits on.
  Builder.prototype.applyCoupon = function () {
    this.couponApplied = true;
    try {
      window.sessionStorage.setItem(COUPON_KEY, this.couponKey);
    } catch (e) { /* storage unavailable */ }
    console.log(LOG, 'placebo apply:', this.couponKey);
    if (this.mode !== 'solo') this.setMode('solo');
    else this.render();
  };

  Builder.prototype.render = function () {
    var self = this;
    var el = this.el;
    var v = this.variant();
    var p0 = v ? v.p : 0;
    var count = this.onCount();
    var n = this.rows.length;

    each(el.cards, function (card) {
      card.classList.toggle('is-selected', card.getAttribute('data-ub-card') === self.mode);
    });
    each(el.radios, function (radio) {
      radio.checked = radio.value === self.mode;
    });

    // Solo card
    setMoney(el.soloPrice, this.couponApplied ? discounted(p0, this.soloPct) : p0);
    setMoney(el.soloWas, p0);
    setHidden(el.soloWas, !this.couponApplied);
    setHidden(el.apply, this.couponApplied);
    setHidden(el.applied, !this.couponApplied);

    // Main product: variant image, colour and price everywhere it appears.
    if (v) {
      each(el.mainImgs, function (img) {
        if (v.i && img.getAttribute('src') !== v.i) img.setAttribute('src', v.i);
      });
      setText(el.mainName, v.n || '');
      setMoney(el.mainPrice, p0);
    }

    // Bundle panel
    setHidden(el.panel, this.mode !== 'bundle');
    this.rows.forEach(function (row, i) {
      row.classList.toggle('is-on', self.on[i]);
      var sw = row.querySelector('[data-ub-switch]');
      if (sw) sw.setAttribute('aria-checked', self.on[i] ? 'true' : 'false');
    });
    if (el.fill) el.fill.style.width = ((count + 1) / (n + 1) * 100) + '%';
    each(el.tierLabels, function (label, idx) {
      label.classList.toggle('is-reached', count + 1 >= idx + 1);
    });

    // Pair fallback: the bundle's percent is derived from the money, so it
    // follows the main variant's price; refresh the target and the labels
    // that quote it (the snippet renders them for the initial variant).
    if (this.pair) {
      var full = this.sumsFor(p0, this.rows.map(function () { return true; }));
      var fullPct = this.shownPct(n, full);
      var soloTier = this.tierPct(0);
      this.maxPct = Math.max(soloTier, fullPct);
      this.maxAt = fullPct > soloTier ? n : 0;
      var lastLabel = el.tierLabels && el.tierLabels.length ? el.tierLabels[el.tierLabels.length - 1] : null;
      if (lastLabel && n > 0) setText(lastLabel, (n + 1) + ' items · ' + fullPct + '%');
      if (el.cardBadge) setText(el.cardBadge, 'Save ' + fullPct + '%');
    }

    var sums = this.sumsFor(p0, this.on);
    var total = sums.total;
    var orig = sums.orig;
    var shown = this.shownPct(count, sums);
    setText(el.badge, 'Save ' + shown + '%');
    setHidden(el.badge, shown <= 0);
    setMoney(el.total, total);
    setMoney(el.was, orig);
    setHidden(el.was, orig === total);
    var left = this.maxAt - count;
    setText(el.hint, left > 0
      ? 'Add ' + left + ' more item' + (left > 1 ? 's' : '') + ' to unlock ' + this.maxPct + '% off'
      : 'Max discount unlocked');

    this.renderQty();
    this.renderAtc();
    this.syncCart();
  };

  Builder.prototype.renderQty = function () {
    var el = this.el;
    if (!el.qty) return;
    var q = this.qty();
    setText(el.qtyValue, String(q));
    if (el.qtyMinus) el.qtyMinus.disabled = q <= 1;
    if (el.qtyPlus) el.qtyPlus.disabled = q >= this.qtyMax();
  };

  // What the cart will charge for the current selection, given the automatic
  // discounts are configured to match (see the snippet).
  Builder.prototype.atcTotal = function () {
    var v = this.variant();
    if (!v) return null;
    var q = this.qty();
    if (this.mode === 'solo') {
      return q * (this.couponApplied ? discounted(v.p, this.soloPct) : v.p);
    }
    return q * this.sumsFor(v.p, this.on).total;
  };

  // The price lives in its own span beside [data-add-to-cart-text], which
  // theme.js keeps rewriting. It is derived from current state on every call and
  // never cached, and it only shows while the module is active and the button
  // is a purchase action. A missing price is a non-event; a wrong one is not.
  Builder.prototype.renderAtc = function () {
    if (!this.form) return;
    var total = this.active ? this.atcTotal() : null;
    each(this.form.querySelectorAll('[data-ub-atc-price]'), function (span) {
      var show = total !== null && purchasable(span.closest('button'));
      setText(span, show ? ' · ' + money(total) : '');
      var display = show ? '' : 'none';
      if (span.style.display !== display) span.style.display = display;
    });
  };

  // The cart guard (dormancy contract): a hidden add-on checkbox is ticked only
  // while the module is displayed, "Bundle & Save" is selected and its switch is on.
  Builder.prototype.syncCart = function () {
    var self = this;
    var take = this.active && this.mode === 'bundle';
    this.rows.forEach(function (row, i) {
      var cb = row.querySelector('[data-addon-item-checkbox]');
      var want = take && self.on[i];
      if (cb && cb.checked !== want) cb.checked = want;
    });
  };

  Builder.prototype.checkActive = function (force) {
    var active = displayed(this.root);
    if (active === this.active && !force) return;
    if (active !== this.active) console.log(LOG, active ? 'active' : 'dormant', this.uid);
    this.active = active;
    // Whatever showed the module (normally the A/B tool's display:block rule),
    // hide what it replaces. The inline check in the snippet already did this
    // at parse time when the rule was on the page by then.
    if (this.form) this.form.classList.toggle('upsell-builder-active', active);
    if (!active) this.closeInfo();
    this.render();
  };

  // "Learn more": the Pair card's product popup, driven the way
  // snippets/product-upsell-pair.liquid drives it (openDetail / closeDetail /
  // paintModal / setHero there). The row's variant is fixed, so the popup holds
  // just that colour's gallery group and nothing needs repainting from state.
  function setHero(modal, thumb) {
    if (!modal || !thumb) return;
    var hero = modal.querySelector('[data-pair-modal-hero]');
    if (!hero) return;
    var src = thumb.getAttribute('data-hero-src');
    if (src) hero.setAttribute('src', src);
    var srcset = thumb.getAttribute('data-hero-srcset');
    if (srcset) hero.setAttribute('srcset', srcset);
    else hero.removeAttribute('srcset');
    var w = thumb.getAttribute('data-hero-w');
    var h = thumb.getAttribute('data-hero-h');
    if (w) hero.setAttribute('width', w);
    if (h) hero.setAttribute('height', h);
    hero.setAttribute('alt', thumb.getAttribute('data-hero-alt') || '');
    var group = thumb.closest('[data-pair-gallery-group]');
    if (!group) return;
    each(group.querySelectorAll('[data-pair-modal-thumb]'), function (t) {
      t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
    });
  }

  function paintModal(modal) {
    var groups = modal.querySelectorAll('[data-pair-gallery-group]');
    if (!groups.length) return;
    var chosen = groups[0];
    each(groups, function (g) { g.hidden = g !== chosen; });
    // Open on the image Shopify attaches to the variant, as the Pair card does.
    setHero(modal, chosen.querySelector('[data-pair-modal-thumb][data-pair-thumb-variant]') ||
                   chosen.querySelector('[data-pair-modal-thumb]'));
  }

  Builder.prototype.openInfo = function (i, trigger) {
    var modal = this.modals ? this.modals[i] : null;
    if (!modal) return;
    // One product popup open at a time, page-wide: the Pair card parks its own
    // close on this body expando; whoever opens next runs it.
    var prevClose = document.body.__upsellPairCloseOpenModal;
    if (this.openModal !== modal && typeof prevClose === 'function') prevClose();
    paintModal(modal);
    this.modalTrigger = trigger || document.activeElement;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    modal.hidden = false;
    this.openModal = modal;
    document.body.__upsellPairCloseOpenModal = this.boundCloseInfo;
    document.body.classList.add('pair-modal-open');
    var dialog = modal.querySelector('[data-pair-modal-dialog]');
    if (dialog) dialog.focus();
    console.log(LOG, 'learn more', i);
  };

  Builder.prototype.closeInfo = function () {
    var modal = this.openModal;
    if (!modal) return;
    this.openModal = null;
    if (document.body.__upsellPairCloseOpenModal === this.boundCloseInfo) {
      document.body.__upsellPairCloseOpenModal = null;
    }
    modal.hidden = true;
    // Only the last popup still open may drop the shared scroll lock.
    if (!document.querySelector('[data-pair-modal]:not([hidden])')) {
      document.body.classList.remove('pair-modal-open');
    }
    var back = this.modalTrigger;
    this.modalTrigger = null;
    if (back) {
      if (back.hasAttribute && back.hasAttribute('data-ub-more')) back.setAttribute('aria-expanded', 'false');
      if (back.focus) back.focus();
    }
  };

  Builder.prototype.bind = function () {
    var self = this;
    var root = this.root;

    root.addEventListener('click', function (evt) {
      var t = evt.target;
      var more = t.closest('[data-ub-more]');
      if (more) {
        evt.preventDefault();
        self.openInfo(self.rows.indexOf(more.closest('[data-ub-row]')), more);
        return;
      }
      if (t.closest('[data-ub-apply]')) {
        evt.preventDefault();
        self.applyCoupon();
        return;
      }
      // The whole row toggles, as designed; the switch is its keyboard control.
      var row = t.closest('[data-ub-row]');
      if (row) {
        evt.preventDefault();
        self.toggle(self.rows.indexOf(row));
        return;
      }
      var card = t.closest('[data-ub-card]');
      if (card && !t.closest('.ub-card__input')) self.setMode(card.getAttribute('data-ub-card'));
    });

    // Arrow keys inside the radio group.
    root.addEventListener('change', function (evt) {
      var t = evt.target;
      if (t.classList && t.classList.contains('ub-card__input') && t.checked) self.setMode(t.value);
    });

    // Product popups: bound on each popup root (it lives under <body>, outside
    // the module), as the Pair card binds its own. Close button, the overlay,
    // or Escape closes; a thumbnail swaps the hero image.
    this.boundCloseInfo = function () { self.closeInfo(); };
    each(this.modals, function (modal) {
      if (!modal) return;
      modal.addEventListener('click', function (evt) {
        var t = evt.target;
        if (!t || !t.closest) return;
        if (t.closest('[data-pair-modal-close]') || (t.hasAttribute && t.hasAttribute('data-pair-modal-overlay'))) {
          evt.preventDefault();
          self.closeInfo();
          return;
        }
        var thumb = t.closest('[data-pair-modal-thumb]');
        if (!thumb) return;
        evt.preventDefault();
        setHero(modal, thumb);
      });
    });
    document.addEventListener('keydown', function (evt) {
      if ((evt.key === 'Escape' || evt.key === 'Esc') && self.openModal) self.closeInfo();
    });

    // The moment the module becomes displayed, whatever did it (the A/B tool's
    // stylesheet rule, a class, an ancestor being shown), its CSS animation
    // starts and this fires. Catches rules no attribute observer can see.
    root.addEventListener('animationstart', function (evt) {
      if (evt.target === root && evt.animationName === 'ub-shown') self.checkActive();
    });
    // Being hidden again has no event of its own (a rule removed, a stylesheet
    // emptied, an ancestor hidden). A displayed module has a box and a hidden
    // one has none, so a size observer sees both directions, whatever caused
    // them. Checked on the next frame so the class change never re-enters it.
    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        window.requestAnimationFrame(function () { self.checkActive(); });
      }).observe(root);
    }
    // Back/forward cache and form restoration can bring checkboxes back ticked.
    window.addEventListener('pageshow', function () { self.checkActive(true); });

    // Stepper, rendered by product-form inside .product__action.
    if (this.el.qty) {
      this.el.qty.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-ub-qty-minus]')) self.setQty(self.qty() - 1);
        else if (evt.target.closest('[data-ub-qty-plus]')) self.setQty(self.qty() + 1);
      });
    }

    if (this.form) {
      // Quantity changed by anything else.
      this.form.addEventListener('change', function (evt) {
        var t = evt.target;
        if (t.matches && t.matches('[name="quantity"], [data-quantity-select]')) {
          self.renderQty();
          self.renderAtc();
        }
      });

      // Last-moment guard: re-derive the cart checkboxes right before theme.js
      // collects them. A capture listener on the form runs before the button's
      // own listeners (theme.js and the Extend warranty interceptor).
      this.form.addEventListener('click', function (evt) {
        if (evt.target.closest && evt.target.closest('[data-add-to-cart]')) {
          self.checkActive();
          self.syncCart();
        }
      }, true);

      // theme.js rewrites the button label and disabled state on every variant
      // change; follow it rather than predict it.
      var atcObserver = new MutationObserver(function () { self.renderAtc(); });
      each(this.form.querySelectorAll('[data-add-to-cart]'), function (btn) {
        atcObserver.observe(btn, { attributes: true, attributeFilter: ['disabled', 'class'] });
        var label = btn.querySelector('[data-add-to-cart-text]');
        if (label) atcObserver.observe(label, { childList: true, characterData: true, subtree: true });
      });
    }

    // Main-product variant changes: theme.js triggers a jQuery event on the section.
    if (window.jQuery) {
      window.jQuery(this.container).on('variantChange.upsellBuilder', function (evt) {
        if (evt && evt.variant && evt.variant.id) {
          self.variantId = String(evt.variant.id);
          self.render();
        }
      });
    }

    // Activation can arrive as a class on <html>/<body>, as the module's inline
    // style being removed, or as an ancestor being shown (the v2 buy box).
    var activeObserver = new MutationObserver(function () { self.checkActive(); });
    for (var node = root; node && node.nodeType === 1; node = node.parentElement) {
      activeObserver.observe(node, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    }
  };

  var instances = [];

  function initAll() {
    each(document.querySelectorAll('[data-upsell-builder]'), function (root) {
      if (root.__upsellBuilder) return;
      root.__upsellBuilder = new Builder(root);
      instances.push(root.__upsellBuilder);
    });
  }

  // A stylesheet added to <head> later (an A/B tool's) can show or hide the
  // module without touching any attribute the per-instance observers watch.
  function watchHead() {
    if (!document.head || !window.MutationObserver) return;
    new MutationObserver(function () {
      instances.forEach(function (b) { if (b.el) b.checkActive(); });
    }).observe(document.head, { childList: true });
  }

  function start() {
    initAll();
    watchHead();
  }

  window.RayconUpsellBuilder = { initAll: initAll, instances: instances };

  // This file is deferred but sits in the page body, ahead of theme.js in
  // document order, so it runs first. Wait for DOMContentLoaded, which fires
  // after every deferred script, so slate's money formatter exists for the
  // first render.
  if (document.readyState === 'loading' || !(window.slate && slate.Currency)) {
    if (document.readyState === 'complete') start();
    else document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
